import React, { useState, useEffect, useMemo } from 'react';
import './ContactForm.css';
import axios from 'axios';
import Reaptcha from 'reaptcha';

const ContactForm = () => {

  const categories = useMemo(() => {
    return [
      'Sales',
      'Waiting Room Configuration',
      'Live issues / troubleshooting',
      'Integrations and Implementations',
      'Billing',
      'Account',
      'Bug Report',
      'Other'
    ];
  }, [])

  const site_key = '6Le8B8oiAAAAAKlMvlDKYwI87gbFpnjYWex9FnO6';
  const [captchaLoaded, setCaptchaLoaded] = useState(false);
  const [isInvalid, setIsInvalid] = useState();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState([]);

  const [errors, setErrors] = useState({
    email: '',
    subject: '',
    message: '',
    category: '',
    recaptchaToken: '',

  });

  let initState = {
    email: {
      value: '',
      touched: false
    },
    name: {
      value: '',
      touched: false
    },
    subject: {
      value: '',
      touched: false
    },
    message: {
      value: '',
      touched: false
    },
    category: {
      value: 'Select a category',
      touched: false
    },
    recaptchaToken: {
      value: '',
      touched: false
    }
  }

  const [values, setValues] = useState(initState);

  const handleFieldChange = (name, value) => {
    setValues({
      ...values,
      [name]: {
        value,
        touched: true
      }
    });
  }

  const handleFieldOnBlur = (name, value) => {
    setValues({
      ...values,
      [name]: {
        value,
        touched: true
      }
    });
  }

  const onRecaptchaLoad = () => {
    setCaptchaLoaded(true)
  }

  const resetForm = (e) => {
    e.preventDefault();
    setValues({
      ...initState
    });
    setSubmitError([]);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    let payload = {};

    for (const key in values) {
      if (Object.hasOwnProperty.call(values, key)) {
        payload[key] = values[key].value.trim();
      }
    }

    let recaptcha_config = {
      method: 'post',
      url: `${process.env.REACT_APP_CREATE_TICKET_API}contact-form`,
      headers: { 'Content-Type': 'application/json' },
      data: JSON.stringify({ ...payload }),
    };

    try {
      let response = await axios(recaptcha_config);
      if (response.data.success) {
        setSubmitted(true);
        setSubmitting(false);
      } else {
        throw new Error('Error submitting form');
      }
    } catch (error) {
      setSubmitting(false);
      setSubmitError(['There was an error submitting your form','Please try again later.'])
      return { data: { success: false } }
    }
  }

  useEffect(() => {
    const validate = () => {
      let validation_errors = {};

      if (!values.email.value || values.email.value === '') {
        validation_errors.email = 'Your email is required';
      } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email.value)) {
        validation_errors.email = 'Invalid email address';
      }

      if (!values.name.value || values.name.value === '') {
        validation_errors.name = 'Your name is required';
      }

      if (!values.subject.value || values.subject.value === '') {
        validation_errors.subject = 'A subject is required';
      }

      if (!values.message.value || values.message.value === '') {
        validation_errors.message = 'A message is required';
      }

      if (categories.indexOf(values.category.value) === -1) {
        validation_errors.category = 'A category is required';
      }
      if (!values.recaptchaToken.value || values.recaptchaToken.value === '') {
        validation_errors.recaptchaToken = 'Please complete the ReCaptcha';
      }


      setErrors(validation_errors);

    }
    if (captchaLoaded) {
      validate();
    }

  }, [categories, values, captchaLoaded]);


  useEffect(() => {
    const isValid = () => {
      let numberOfErrors = 0;

      for (const error in errors) {
        if (Object.hasOwnProperty.call(errors, error)) {
          numberOfErrors++;
        }
      }

      setIsInvalid(numberOfErrors > 0);
    }

    isValid()
  }, [errors]);


  return (


    <div className="ContactForm">
      {!submitted

        ?
        <form onSubmit={handleSubmit}>
          <p className='required-message'>All fields are required</p>
          <label htmlFor="email-address" className="form-label">Email Address</label>
          <input
            id="email-address"
            name="email"
            type="text"
            className="feedback-input"
            placeholder="Email"
            onChange={(e) => { handleFieldChange('email', e.target.value) }}
            onBlur={(e) => { handleFieldOnBlur('email', e.target.value) }}
            value={values.email.value}
          />
          {errors.email && values.email.touched ? <ErrorMessage message={errors.email} /> : null}

          <label htmlFor="name" className="form-label">Name</label>
          <input
            id="name"
            name="name"
            type="text"
            className="feedback-input"
            placeholder="Name"
            onChange={(e) => { handleFieldChange('name', e.target.value) }}
            onBlur={(e) => { handleFieldOnBlur('name', e.target.value) }}
            value={values.name.value}
          />
          {errors.name && values.name.touched ? <ErrorMessage message={errors.name} /> : null}

          <label htmlFor="subject" className="form-label">Subject</label>
          <input
            id="subject"
            name="subject"
            type="text"
            className="feedback-input"
            placeholder="Subject"
            onChange={(e) => { handleFieldChange('subject', e.target.value) }}
            onBlur={(e) => { handleFieldOnBlur('subject', e.target.value) }}
            value={values.subject.value}
          />
          {errors.subject && values.subject.touched ? <ErrorMessage message={errors.subject} /> : null}

          <label htmlFor="message" className="form-label">Message</label>
          <textarea
            rows="4"
            id="message"
            name="message"
            className="feedback-textarea"
            placeholder="Message"
            onChange={(e) => { handleFieldChange('message', e.target.value) }}
            onBlur={(e) => { handleFieldOnBlur('message', e.target.value) }}
            value={values.message.value}
          ></textarea>
          {errors.message && values.message.touched ? <ErrorMessage message={errors.message} /> : null}

          <div className="category">
            <label htmlFor="category" className="form-label">Category</label>
            <select
              id="category"
              name="category"
              className="feedback-input"
              placeholder="Category"
              onChange={(e) => { handleFieldChange('category', e.target.value) }}
              onBlur={(e) => { handleFieldOnBlur('category', e.target.value) }}
              value={values.category.value}
            >
              <option>Select a category</option>
              {categories.map((category, index) => {
                return <option key={index} value={category}>{category}</option>
              })}
            </select>
            {errors.category && values.category.touched ? <ErrorMessage message={errors.category} /> : null}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
            <Reaptcha
              sitekey={site_key}
              onVerify={(recaptchaResponse) => {
                handleFieldChange('recaptchaToken', recaptchaResponse)
              }}
              onExpire={() => {
                handleFieldChange('recaptchaToken', '')
              }}
              onLoad={onRecaptchaLoad}
            />
          </div>
          {errors.recaptchaToken && values.recaptchaToken.touched ? <ErrorMessage message={errors.recaptchaToken} /> : null}


          {submitError.length
          ?
          <div className='error-messaging'>
            {submitError.map((errorMessage, index)=>{
              return <p key={index}>{errorMessage}</p>
  
            })}
          </div>
          :
          null}

          <div className="actions">

          {submitting ?
            <button type="submit" className='submit-button'
              disabled={true}
            >
              Submitting <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-loader"><line x1="12" y1="2" x2="12" y2="6"></line><line x1="12" y1="18" x2="12" y2="22"></line><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line><line x1="2" y1="12" x2="6" y2="12"></line><line x1="18" y1="12" x2="22" y2="12"></line><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line></svg>
            </button>
          :
            <button type="submit" className='submit-button'
              disabled={isInvalid || submitting}
            >
            Submit
            </button>

          }
              
              
            <button className="reset-button" onClick={(e) => {
              resetForm(e);
            }}>Reset</button>
          </div>
        </form>
        :
        <div className="form__submitted">
          <p className="submitted-message">Thank your for your message. We will respond shortly.</p>
          <button className="reset-button" onClick={(e) => {
            resetForm(e);
            setSubmitted(false);
          }}>Reload</button>
          
        </div>
      }

    </div>
  );
}

export default ContactForm;


const ErrorMessage = ({ message }) => {
  return <div className='error-message'>{message}</div>
}
